import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useGetMonthlyMarketingReportQuery } from 'store/services/admin/analyticsApi';
import { MarketingSection } from 'types/interfaces/admin/analytics';
import { useAuthedImageBlob } from './useAuthedImageBlob';
import { buildMonthOptions, marketingFileName, monthLabel } from './marketingMonths';
import './MarketingTab.css';

/** Image clipboard support (Safari / Chrome / Firefox 127+); otherwise only Download shows. */
const canCopyImage = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as any).ClipboardItem !== 'undefined' &&
  !!navigator.clipboard?.write;

const COPIED_MS = 1500;

const MarketingSectionCard: React.FC<{ section: MarketingSection; month: string }> = ({ section, month }) => {
  const { objectUrl, blob, blobPromise, error } = useAuthedImageBlob(section.insufficientData ? null : section.imageUrl);
  const [copied, setCopied] = useState<'image' | 'caption' | null>(null);

  useEffect(() => {
    if (!copied) return undefined;
    const t = window.setTimeout(() => setCopied(null), COPIED_MS);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (section.insufficientData) {
    return (
      <div className="chart-card marketing-section marketing-section--insufficient" data-section={section.key} data-testid={`marketing-section-${section.key}`}>
        <h2 className="marketing-section__title">{section.title}</h2>
        <p className="marketing-section__note">{section.insufficientReason || 'Not enough data this month.'}</p>
      </div>
    );
  }

  const copyImage = () => {
    // The promise goes into ClipboardItem synchronously inside the click, which
    // Safari requires (TradeMapShareModal.tsx:122-139 pattern).
    const source = blob ? Promise.resolve(blob) : blobPromise;
    if (!source) return;
    const pngBlob = source.then((b) => (b.type === 'image/png' ? b : new Blob([b], { type: 'image/png' })));
    navigator.clipboard
      .write([new (window as any).ClipboardItem({ 'image/png': pngBlob })])
      .then(() => setCopied('image'))
      .catch(() => toast.error('Could not copy the image. Use Download PNG instead.'));
  };

  const downloadImage = () => {
    if (!objectUrl) return;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = marketingFileName(section.key, month);
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyCaption = () => {
    if (!section.caption) return;
    navigator.clipboard
      .writeText(section.caption)
      .then(() => setCopied('caption'))
      .catch(() => toast.error('Could not copy the caption.'));
  };

  return (
    <div className="chart-card marketing-section" data-section={section.key} data-testid={`marketing-section-${section.key}`}>
      <h2 className="marketing-section__title">{section.title}</h2>
      <div className="marketing-section__image">
        {objectUrl ? (
          <img src={objectUrl} alt={section.title} width={1200} height={675} />
        ) : (
          <div className="marketing-section__placeholder">{error ? 'Image failed to load.' : 'Loading image...'}</div>
        )}
      </div>
      {section.caption && <p className="marketing-section__caption">{section.caption}</p>}
      <div className="marketing-section__actions">
        {canCopyImage() && (
          <button type="button" className="marketing-section__btn" onClick={copyImage} disabled={!objectUrl}>
            {copied === 'image' ? 'Copied' : 'Copy image'}
          </button>
        )}
        <button type="button" className="marketing-section__btn" onClick={downloadImage} disabled={!objectUrl}>
          Download PNG
        </button>
        <button type="button" className="marketing-section__btn" onClick={copyCaption} disabled={!section.caption}>
          {copied === 'caption' ? 'Copied' : 'Copy caption'}
        </button>
      </div>
    </div>
  );
};

/**
 * Admin > Analytics > Marketing: ten monthly sections as finished 1200x675
 * images with captions, ready to paste into Buffer. Everything is rendered by
 * the backend; this tab never computes or reformats a value.
 */
const MarketingTab: React.FC = () => {
  // Undefined until the user picks: the first request sends no month and the
  // backend chooses the most recent completed calendar month (UTC).
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(undefined);
  const [defaultMonth, setDefaultMonth] = useState<string | null>(null);
  const { data, isLoading, isFetching, isError } = useGetMonthlyMarketingReportQuery({ month: selectedMonth });

  useEffect(() => {
    if (!defaultMonth && data?.month && selectedMonth === undefined) setDefaultMonth(data.month);
  }, [data?.month, defaultMonth, selectedMonth]);

  const options = useMemo(() => (defaultMonth ? buildMonthOptions(defaultMonth) : []), [defaultMonth]);
  const shownMonth = selectedMonth ?? defaultMonth ?? data?.month ?? '';

  return (
    <div className="marketing-tab">
      <div className="marketing-tab__header">
        <label className="marketing-tab__picker">
          <span>Month</span>
          <select
            value={shownMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            disabled={options.length === 0}
            aria-label="Report month"
          >
            {options.length === 0 && shownMonth && <option value={shownMonth}>{monthLabel(shownMonth)}</option>}
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {data && (
          <span className="marketing-tab__status">
            {monthLabel(data.month)}
            {data.isPartial ? ' (so far)' : ''}
            {isFetching ? ' · updating…' : ''}
          </span>
        )}
      </div>

      {isLoading && <p className="marketing-tab__message">Loading this month's report...</p>}
      {isError && !isLoading && <p className="marketing-tab__message">The report could not be loaded.</p>}

      {data && (
        <div className="analytics-charts-grid marketing-tab__grid">
          {data.sections.map((section) => (
            <MarketingSectionCard key={`${data.month}-${section.key}`} section={section} month={data.month} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketingTab;
